import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { setSessionExpiredHandler } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const UserContext = createContext(null);
export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const { setLang } = useLang();
  const [authEnabled, setAuthEnabled] = useState(false);
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const me = await api.get("/auth/me");
    setUser(me.data);
    if (me.data?.voorkeurstaal) setLang(me.data.voorkeurstaal);
    return me.data;
  }, [setLang]);

  const init = useCallback(async () => {
    setLoading(true);
    try {
      const status = await api.get("/auth/status");
      const enabled = status.data.auth_enabled;
      setAuthEnabled(enabled);
      if (enabled) {
        // CRITICAL: bij terugkeer van Google OAuth (session_id in fragment) de /me-check
        // overslaan; AuthCallback wisselt eerst de session_id in en zet de sessie op.
        if (window.location.hash?.includes("session_id=")) {
          setUser(null);
          return;
        }
        const token = localStorage.getItem("zk_token");
        if (token) {
          try { await loadMe(); } catch { localStorage.removeItem("zk_token"); setUser(null); }
        } else {
          setUser(null);
        }
      } else {
        if (!localStorage.getItem("zk_user_id")) localStorage.setItem("zk_user_id", "demo-werknemer");
        await loadMe();
        const us = await api.get("/users");
        setUsers(us.data);
      }
    } finally {
      setLoading(false);
    }
  }, [loadMe]);

  useEffect(() => { init(); }, [init]);

  // Centrale afhandeling van sessieverval (401): onthoud de huidige pagina, log de gebruiker
  // netjes uit en toon één duidelijke melding. Bij wachtwoord-herlogin blijft de URL staan,
  // dus keert de gebruiker automatisch terug naar dezelfde pagina; voor Google-herlogin
  // gebruikt AuthCallback het opgeslagen terugkeerpad.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      const path = window.location.pathname + window.location.search;
      if (!path.startsWith("/voorwaarden") && !path.startsWith("/privacy")) {
        sessionStorage.setItem("zk_return_to", path);
      }
      setUser(null);
      toast.error("Je sessie is verlopen. Log opnieuw in om verder te gaan.");
    });
  }, []);

  // Auth mode (v1.1)
  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("zk_token", r.data.access_token);
    setUser(r.data.user);
    if (r.data.user?.voorkeurstaal) setLang(r.data.user.voorkeurstaal);
  };
  const completeGoogleLogin = async (sessionId) => {
    const r = await api.post("/auth/google/session", { session_id: sessionId });
    localStorage.setItem("zk_token", r.data.session_token);
    setUser(r.data.user);
    if (r.data.user?.voorkeurstaal) setLang(r.data.user.voorkeurstaal);
    return r.data.user;
  };
  const acceptTerms = async () => {
    const r = await api.post("/auth/accept-terms", { voorwaarden_akkoord: true, privacy_akkoord: true, ai_besef_akkoord: true });
    setUser(r.data.user);
    return r.data.user;
  };
  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    localStorage.setItem("zk_token", r.data.access_token);
    setUser(r.data.user);
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    localStorage.removeItem("zk_token");
    setUser(null);
  };
  const forgotPassword = async (email) => api.post("/auth/forgot-password", { email });

  // Demo mode (testfase, AUTH_ENABLED=false)
  const switchUser = async (id) => {
    localStorage.setItem("zk_user_id", id);
    setLoading(true);
    await loadMe();
    setLoading(false);
  };

  return (
    <UserContext.Provider value={{
      authEnabled, user, users, loading,
      login, register, logout, forgotPassword, switchUser,
      completeGoogleLogin, acceptTerms,
    }}>
      {children}
    </UserContext.Provider>
  );
};
